import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  ObfuscationService,
  ObfuscationType,
} from '@principle-theorem/ng-management-tools';
import {
  DownloadReportComponent,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import { ReportingPermissions } from '@principle-theorem/principle-core/features';
import {
  CustomChartType,
  ICustomReport,
  ICustomReportChart,
  ICustomReportFilter,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomReportFilter,
  REPORT_BUILDER_DATA_SOURCES,
  resolveDataSource,
} from '@principle-theorem/reporting';
import {
  filterUndefined,
  firstValueFrom,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import {
  getChartDisplay,
  IChartDisplay,
} from '../../../report-builder/report-builder-chart-filters/report-builder-chart-section/report-builder-chart/report-builder-chart-display';
import { type LabelTransformFn } from '../../../../models/report/charts/data-transformers/dc-data-transformer';
import { ReportBuilderChartComponent } from '../../../report-builder/report-builder-chart-filters/report-builder-chart-section/report-builder-chart/report-builder-chart.component';
import {
  ReportBuilderTableChart,
  ReportBuilderTableChartCSV,
} from '../../../report-builder/report-builder-chart-filters/report-builder-chart-section/report-builder-table-chart/report-builder-table-chart-csv';
import { ReportBuilderStore } from '../../../report-builder/report-builder.store';
import { ReportEditorService } from '../../../report-builder/report-editor.service';

@Component({
  selector: 'pr-custom-report-charts',
  templateUrl: './custom-report-charts.component.html',
  styleUrls: ['./custom-report-charts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDivider,
    MatIcon,
    MatIconButton,
    MatMenuModule,
    MatRipple,
    MatTooltip,
    NgCommonModule,
    ReportBuilderChartComponent,
    DownloadReportComponent,
  ],
})
export class CustomReportChartsComponent {
  private _organisation = inject(OrganisationService);
  private _reportEditor = inject(ReportEditorService);
  private _obfuscation = inject(ObfuscationService);
  private _labelTransformFn: LabelTransformFn;
  store = inject(ReportBuilderStore);
  hasEditPermissions$: Observable<boolean>;

  customReport = input.required<WithRef<ICustomReport>>();
  readonly = input(false);

  constructor() {
    this.hasEditPermissions$ = this._organisation.userHasPermission$(
      ReportingPermissions.ReportingCustomReportEdit
    );
    this._labelTransformFn = (label: string, piiType?: string): string =>
      this._obfuscation.transform(
        label,
        (piiType as ObfuscationType) ?? 'name'
      );
  }

  getChartDisplay$(chart: ICustomReportChart): Observable<IChartDisplay> {
    return combineLatest([this.store.ndx$, this._obfuscation.isActive$]).pipe(
      withLatestFrom(this.store.dataSource$),
      map(([[ndx, _isActive], dataSource]) => {
        const display = dataSource
          ? getChartDisplay(dataSource, chart, ndx, this._labelTransformFn)
          : undefined;
        return display ?? { chart };
      })
    );
  }

  getChartClasses(chart: ICustomReportChart): string {
    const wideCharts = [
      CustomChartType.Line,
      CustomChartType.Bar,
      CustomChartType.Table,
      CustomChartType.Column,
      CustomChartType.SanKey,
      CustomChartType.Histogram,
      CustomChartType.StackedBar,
      CustomChartType.StackedArea,
    ];
    const colSpan = wideCharts.includes(chart.type) ? 'chart-lg' : 'chart-sm';

    return compact([colSpan]).join(' ');
  }

  getFilterSummary$(filter: ICustomReportFilter): Observable<string> {
    return this.store.dataSource$.pipe(
      map((dataSource) => dataSource?.factTable),
      filterUndefined(),
      map((factTable) => CustomReportFilter.getSummary(factTable, filter))
    );
  }

  isTable(display: IChartDisplay): boolean {
    return display.chart.type === CustomChartType.Table;
  }

  async addChart(index?: number): Promise<void> {
    const customReport = this.customReport();
    const dataSource = resolveDataSource(
      customReport.dataSource,
      REPORT_BUILDER_DATA_SOURCES
    );
    if (!dataSource) {
      return;
    }
    const filteredData = await snapshot(
      this.store.results$.pipe(map((results) => results?.filtered ?? []))
    );
    const ndx = await firstValueFrom(this.store.ndx$);

    const updatedChart = await this._reportEditor.editChart(
      dataSource,
      undefined,
      filteredData,
      ndx
    );
    if (!updatedChart) {
      return;
    }
    await this.store.addChart(updatedChart, undefined, index);
  }

  async moveChartLeft(display: IChartDisplay, sectionName: string): Promise<void> {
    await this.store.moveChart(-1, display.chart, sectionName);
  }

  async moveChartRight(display: IChartDisplay, sectionName: string): Promise<void> {
    await this.store.moveChart(1, display.chart, sectionName);
  }

  async deleteChart(display: IChartDisplay): Promise<void> {
    await this.store.deleteChart(display.chart);
  }

  async editChart(chart: ICustomReportChart): Promise<void> {
    if (this.readonly()) {
      return;
    }
    await this.store.editChart(chart);
  }

  async editTable(chart: ICustomReportChart): Promise<void> {
    await this.store.editTable(chart);
  }

  getCSVData(display: IChartDisplay): object[] {
    return ReportBuilderTableChart.getCSVData(display);
  }

  getCSVTranslator$(
    display: IChartDisplay
  ): Observable<ReportBuilderTableChartCSV | undefined> {
    return this.store.dataSource$.pipe(
      map((dataSource) =>
        ReportBuilderTableChart.getCSVTranslator$(display, dataSource)
      )
    );
  }
}
